import { type MutableRefObject, useCallback } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

/**
 * Cut, Select All and Delete routed to whichever mode is live: inside a text-edit session they act
 * on the text, otherwise Cut falls through to the object cut. Also whether a session is live (even
 * with focus parked on a keepalive control) and handing focus back to its editor.
 */
export function useTextEditRouting({
  internalShapeManagerRef,
  cutSelectionToClipboard,
  handleSelectChainMembers,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  cutSelectionToClipboard: () => boolean
  handleSelectChainMembers: (frameId: string) => boolean
}) {
  /**
   * Cut for whichever mode is live: text-editing mode cuts the selected *text*, object mode cuts
   * the selected objects (WMPCM-7004 — a cut while editing must never remove the frame).
   *
   * Editing state is read from the managers' own editing ids rather than the
   * `isShapeTextEditing`/`isInlineTextEditing` React mirrors, because the synchronous Ctrl+X
   * keydown (the keyboard shortcuts in `use-canvas-keyboard-shortcuts`) can run a frame before
   * those states settle.
   */
  const cutSelectionForActiveMode = useCallback((): boolean => {
    if (internalShapeManagerRef.current?.getEditingShapeTextId()) {
      internalShapeManagerRef.current.execShapeTextCut()
      return true
    }

    return cutSelectionToClipboard()
  }, [cutSelectionToClipboard, internalShapeManagerRef])

  /**
   * True while the shape text editor holds an active session — including when focus has moved
   * off the editable to a `data-text-edit-keepalive` control (the Type panel root, the menubar
   * trigger), which `isEditableTarget` does not match. Canvas shortcuts that would mutate the
   * *object* must consult this, not just the event target, or they hit the frame being edited
   * (WMPCM-7004).
   */
  const isTextEditSessionActive = useCallback((): boolean => {
    return Boolean(internalShapeManagerRef.current?.getEditingShapeTextId())
  }, [internalShapeManagerRef])

  /**
   * Return focus to the text editor holding the live session, so the user's next keystroke
   * reaches it natively instead of the canvas. Used whenever a routed edit can't be performed
   * faithfully (WMPCM-7044).
   */
  const refocusActiveTextEditor = useCallback((): void => {
    internalShapeManagerRef.current?.restoreShapeTextEditorFocus()
  }, [internalShapeManagerRef])

  /**
   * Ctrl+A for whichever mode is live, the mirror of `cutSelectionForActiveMode`. A chain member
   * selects its whole story across every member; a solo frame (or a chain member whose seed
   * declines) selects its own text.
   *
   * Three routes reach this, not one: the window keydown handler when focus has moved off the
   * editor to a `data-text-edit-keepalive` control, the inline-edit context menu's Select All, and
   * the menubar's Edit → Select All via `selectAllForActiveMode` on the handle (WMPCM-6867). A
   * keypress on the textarea itself never gets here — it is stopped there and handled by the
   * editor's own `onSelectChain`/`execSelectAll` branch (WMPCM-9396).
   *
   * Returns whether it actually selected anything, so the menubar caller can tell "handled" from
   * "no live session" instead of falling through to an object select-all mid-sentence.
   */
  const selectAllForActiveTextEdit = useCallback((): boolean => {
    const smgr = internalShapeManagerRef.current
    const editingId = smgr?.getEditingShapeTextId()

    if (!editingId) return false
    if (handleSelectChainMembers(editingId)) return true

    return smgr?.execShapeTextSelectAllInFrame() ?? false
  }, [handleSelectChainMembers, internalShapeManagerRef])

  /**
   * Delete/Backspace for whichever mode is live: text-editing mode removes *text*, object mode
   * removes objects (WMPCM-7044 — the mirror of `cutSelectionForActiveMode` above).
   *
   * This only ever runs when focus has moved off the editor to a `data-text-edit-keepalive`
   * control, because a keypress landing on the editor itself is stopped there
   * (`shape-text-editor/hooks/use-shape-text-keyboard.ts` calls `stopPropagation` on the textarea keydown) and never reaches
   * the canvas handler at all. Falling through in that state deleted the frame being edited.
   *
   * Returns true whenever a session was live — including when the editor deleted nothing. The
   * point of the return value is "the object delete must not run", not "text changed". When the
   * editor could not act (an empty frame, or a caret with nothing to remove on that side), focus
   * is handed back so the user's next press deletes natively.
   */
  const deleteSelectionForActiveMode = useCallback(
    (direction: 'forward' | 'backward'): boolean => {
      const smgr = internalShapeManagerRef.current

      if (smgr?.getEditingShapeTextId()) {
        if (!smgr.execShapeTextDelete(direction)) smgr.restoreShapeTextEditorFocus()

        return true
      }

      return false
    },
    [internalShapeManagerRef]
  )

  /**
   * Delete/Backspace pressed while a text-edit session is live. Bare presses are routed into the
   * editor; a modified press is only handed focus back, because there is no word-delete command
   * to route — Ctrl/Alt+Backspace would remove a single character where the user asked for a
   * word. Either way the *object* delete never runs, which is the point (WMPCM-7044).
   */
  const handleDeleteKeyWhileTextEditing = useCallback(
    (e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>): void => {
      if (e.ctrlKey || e.metaKey || e.altKey) {
        refocusActiveTextEditor()
        return
      }

      deleteSelectionForActiveMode(e.key === 'Delete' ? 'forward' : 'backward')
    },
    [deleteSelectionForActiveMode, refocusActiveTextEditor]
  )

  return {
    cutSelectionForActiveMode,
    isTextEditSessionActive,
    refocusActiveTextEditor,
    selectAllForActiveTextEdit,
    deleteSelectionForActiveMode,
    handleDeleteKeyWhileTextEditing,
  }
}
